import { useCallback, useEffect } from "react";

import { useUser } from "../../entities/users/hooks";
import { actions } from "../../entities/slices/user.slice";
import { useAppDispatch } from "../../entities/utils/useAppDispatch.hook";
import { useFeature } from "../features";
import { ensureStylesheetLink, getDocument } from "../../../utils/web";

import {
  getLegibilityFontStylesheets,
  legibilityFontLinkId,
} from "./legibilityFontStyles";
import { useSetUserAccessibilityPreferences } from "./requests";
import {
  DEFAULT_ACCESSIBILITY_PREFERENCES,
  LEGIBILITY_FONT_OPTIONS,
  type LegibilityFontPreference,
} from "./types";

/**
 * Resolves the effective legibility font for a user based on feature flag and
 * user preference. There is no system/media fallback: the stored preference is
 * applied verbatim when the feature is on.
 */
export const resolveLegibilityFont = (
  featureOn: boolean,
  pref: LegibilityFontPreference,
): LegibilityFontPreference => (featureOn ? pref : "default");

/**
 * Read-only hook that resolves the user's legibility font preference, writes
 * `data-legibility-font` on the <html> element, and appends the chosen font's
 * stylesheet `<link>` on demand.
 *
 * Font links are append-only: once a webfont is fetched there is nothing to
 * gain from removing it, and switching back to "default" is just the data
 * attribute changing.
 */
export const useApplyLegibilityFont = () => {
  const { featureEnabled } = useFeature();
  const user = useUser();

  const preference: LegibilityFontPreference =
    user?.user_accessibility_preferences?.legibility_font ?? "default";

  const resolved = resolveLegibilityFont(
    featureEnabled("accessibility_mode_legibility_font"),
    preference,
  );

  useEffect(() => {
    const doc = getDocument();
    if (!doc) return;
    doc.documentElement.dataset.legibilityFont = resolved;

    if (resolved === "default") return;
    ensureStylesheetLink(
      legibilityFontLinkId(resolved),
      getLegibilityFontStylesheets()[resolved],
    );
  }, [resolved]);

  return { resolved, preference };
};

/**
 * Full legibility font hook: composes the read-only DOM effect with an
 * optimistic setter that persists the user's preference.
 */
export const useLegibilityFont = () => {
  const { resolved, preference } = useApplyLegibilityFont();
  const user = useUser();
  const dispatch = useAppDispatch();
  const { setUserAccessibilityPreferences } =
    useSetUserAccessibilityPreferences();

  // setter for UI to update preferences
  const setLegibilityFont = useCallback(
    (value: LegibilityFontPreference) => {
      // PUT is fire-and-forget and does not sync its response back into Redux.
      // Treat this as an optimistic update
      if (user) {
        dispatch(
          actions.upsert({
            ...user,
            user_accessibility_preferences: {
              ...DEFAULT_ACCESSIBILITY_PREFERENCES,
              ...user.user_accessibility_preferences,
              legibility_font: value,
            },
          }),
        );
      }
      setUserAccessibilityPreferences({ legibility_font: value });
    },
    [dispatch, setUserAccessibilityPreferences, user],
  );

  return {
    resolved,
    preference,
    options: LEGIBILITY_FONT_OPTIONS,
    setLegibilityFont,
  };
};
